import React from 'react';

import { StyleSheet, View } from 'react-native';
import { KeyboardAwareScrollView, KeyboardStickyView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { WEB_MAX_WIDTH_STYLE, modalStyles } from 'components/card-modal';
import { CardModalHeader, ModalHeaderProps } from 'components/card-modal-header';
import { useKeyboardAwareFooter } from 'components/ucl/keyboard-aware-footer';
import { spacing } from 'styles/constants';
import { isWeb } from 'utils/environment';

interface IDrawerPage {
  children: React.ReactNode;
  headerText?: string | null | undefined;
  helmetTitle?: string;
  footer?: React.ReactNode;
  _headerOverride?: React.ReactNode;
  disableWebMaxWidth?: boolean;
  keyboardAwareFooter?: boolean;
}

export const DrawerPage: React.FC<React.PropsWithChildren<IDrawerPage & ModalHeaderProps>> = ({
  children,
  headerText,
  helmetTitle,
  dismissType,
  footer,
  _headerOverride,
  disableWebMaxWidth = false,
  keyboardAwareFooter = false,
}) => {
  const { top, bottom } = useSafeAreaInsets();
  const {
    bottomOffset: keyboardAwareFooterBottomOffset,
    isKeyboardAwareFooterEnabled,
    onFooterLayout: handleFooterLayout,
  } = useKeyboardAwareFooter({ enabled: keyboardAwareFooter });
  const keyboardAwareScrollContainerStyle = keyboardAwareFooterBottomOffset
    ? { paddingBottom: keyboardAwareFooterBottomOffset }
    : null;

  const footerContent = footer && (
    <View
      onLayout={handleFooterLayout}
      style={[modalStyles.footerWrap, { paddingBottom: bottom || spacing.$4 }]}
    >
      <View style={[modalStyles.footerInner, WEB_MAX_WIDTH_STYLE]}>{footer}</View>
    </View>
  );
  const finalFooter =
    footerContent && isKeyboardAwareFooterEnabled ? (
      <KeyboardStickyView style={modalStyles.keyboardAwareFooter}>
        <View pointerEvents="none" style={modalStyles.keyboardAccessoryFooterBackground} />
        {footerContent}
      </KeyboardStickyView>
    ) : (
      footerContent
    );

  return (
    <View style={{ flex: 1, paddingTop: top - 16 }}>
      {_headerOverride || (
        <CardModalHeader
          {...{
            headerText,
            helmetTitle,
            dismissType: dismissType || 'back',
            containerStyle: { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
          }}
        />
      )}
      <KeyboardAwareScrollView
        bottomOffset={keyboardAwareFooterBottomOffset}
        contentContainerStyle={[modalStyles.scrollContainer, keyboardAwareScrollContainerStyle]}
        style={[modalStyles.scrollStyle, styles.scrollStyle]}
      >
        <View
          style={[
            modalStyles.wrappedChildren,
            styles.wrappedChildren,
            bottom ? { paddingBottom: bottom } : {},
            disableWebMaxWidth ? null : WEB_MAX_WIDTH_STYLE,
          ]}
        >
          {children}
        </View>
      </KeyboardAwareScrollView>
      {finalFooter}
    </View>
  );
};

const styles = StyleSheet.create({
  wrappedChildren: {
    paddingHorizontal: spacing.$4,
    height: isWeb ? '100%' : undefined,
  },
  scrollStyle: isWeb
    ? {
        alignSelf: 'center',
      }
    : {},
});

export default DrawerPage;
